/**
 * What a cook's recipe library is: the shared catalogue plus their saved
 * recipes, minus the ones they hid, one per title.
 *
 * The app builds it in `unifiedRecipeService` (client SDK) and the weekly
 * email in `api/_lib/recipeLibrary.ts` (Admin SDK). The reads differ; the
 * answer must not. The email used to take the first 100 saved recipes and no
 * catalogue at all, so it proposed from a different library than the one the
 * cook plans from in the app — never a catalogue dinner, and for a cook with
 * 356 saved recipes, whichever 100 Firestore returned first.
 *
 * Pure: no Firebase and no react-native, so both sides import it. The types
 * are imported type-only, which the build erases.
 */
import type { RecipeIndexEntry, SharedRecipe, UserSavedRecipe } from './unifiedRecipeService';
import { indexIngredientLines } from '../utils/recipeContent';

/** Platform-specific image path repair; the app passes its own, the server none. */
export type ImageFixer = (image?: string) => string | undefined;
const asIs: ImageFixer = (image) => image;

/**
 * Normalize title for deduplication
 * "Crème Brûlée" → "cremebrulee"
 */
export function normalizeTitle(title: string): string {
  return title
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '') // Remove accents
    .replace(/[^a-z0-9]/g, '') // Keep only alphanumeric
    .trim();
}

/**
 * One shared-catalog document, as the recipe index sees it.
 *
 * Every field dropped here is dropped for the whole library, silently —
 * `ingredientGroups` (#496), `enrichmentAttemptCount` (#668) and
 * `translatedTitles` were each lost this way. `translatedTitles` in particular
 * is written into the catalog document (api/_lib/sharedCatalogDoc.ts) and read
 * by `resolveRecipeTitle` on every browse card, so leaving it out made the
 * French titles unreachable for all base recipes while the personal ones worked.
 */
export function catalogueIndexEntry(data: SharedRecipe, fixImage: ImageFixer = asIs): RecipeIndexEntry {
  return {
    id: data.id,
    title: data.title,
    image: fixImage(data.image),
    servings: data.servings,
    servingsDisplay: data.servingsDisplay,
    readyInMinutes: data.readyInMinutes,
    handsOnMinutes: data.handsOnMinutes,
    readyInMinutesMin: data.readyInMinutesMin,
    dishTypes: data.dishTypes || [],
    cuisines: data.cuisines || [],
    diets: data.diets || [],
    ingredients: indexIngredientLines(data),
    searchText: data.searchText || data.title.toLowerCase(),
    originalLanguage: data.originalLanguage,
    source: 'base',
    sourceUrl: data.sourceUrl,
    lastScrapedAt: data.lastScrapedAt,
    enrichmentAttemptCount: (data as any).enrichmentAttemptCount,
    sourceRating: (data as any).sourceRating,
    sourceRatingCount: (data as any).sourceRatingCount,
    translatedTitles: (data as any).translatedTitles,
    languages: (data as any).languages,
  };
}

/** One of the cook's saved recipes, as the recipe index sees it. */
export function savedIndexEntry(id: string, data: UserSavedRecipe, fixImage: ImageFixer = asIs): RecipeIndexEntry {
  return {
    id,
    title: data.title,
    image: fixImage(data.image),
    servings: data.servings,
    servingsDisplay: data.servingsDisplay,
    readyInMinutes: data.readyInMinutes,
    handsOnMinutes: data.handsOnMinutes,
    readyInMinutesMin: data.readyInMinutesMin,
    dishTypes: data.dishTypes || [],
    cuisines: data.cuisines || [],
    diets: data.diets || [],
    ingredients: indexIngredientLines(data),
    searchText: data.searchText || data.title.toLowerCase(),
    originalLanguage: data.originalLanguage,
    savedAt: data.savedAt,
    // A saved copy is never the catalogue. `isOfferedTo` reads a missing
    // source as a shared recipe and hides it from a reader of the other
    // language; three saved documents (all test fixtures) carry none.
    source: data.source ?? 'app',
    newsletterName: (data as any).newsletterName,
    sourceUrl: (data as any).sourceUrl,
    lastScrapedAt: (data as any).lastScrapedAt,
    enrichmentAttemptCount: (data as any).enrichmentAttemptCount,
    sourceRating: (data as any).sourceRating,
    sourceRatingCount: (data as any).sourceRatingCount,
    translatedTitles: (data as any).translatedTitles,
  };
}

/**
 * The library: saved recipes first (the cook's own copy of a title wins over
 * the catalogue's), then the catalogue, hidden ids removed, one entry per
 * normalized title. `saved` is expected newest first, as both loaders read it.
 */
export function mergeRecipeLibrary(
  shared: RecipeIndexEntry[],
  saved: RecipeIndexEntry[],
  hidden: string[],
): RecipeIndexEntry[] {
  const hiddenSet = new Set(hidden);
  const seenNormalized = new Map<string, RecipeIndexEntry>();

  for (const recipe of saved) {
    if (!hiddenSet.has(recipe.id)) {
      seenNormalized.set(normalizeTitle(recipe.title), recipe);
    }
  }

  for (const recipe of shared) {
    if (!hiddenSet.has(recipe.id)) {
      const normalized = normalizeTitle(recipe.title);
      if (!seenNormalized.has(normalized)) {
        seenNormalized.set(normalized, recipe);
      }
    }
  }

  return Array.from(seenNormalized.values());
}
